import React, { useCallback, useEffect, useRef, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import { Dropdown, Form } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ModalBlock from '../../../components/ModalBlock';
import * as aiSystemDocumentService from '../../../../services/aiSystemDocumentService';
import SystemDocumentDetailsModal from './SystemDocumentDetailsModal';
import { patchSystemDocumentPage } from './documentIndexRowState';
import {
  fileTypeOptions,
  formatDateTime,
  getExtractionMethodLabel,
  getResourcePreviewType,
  getSourceKindLabel,
  getStatusMetadata,
  statusOptions,
} from './systemDocumentMetadata';

const INITIAL_QUERY = {
  page: 1,
  pageSize: 25,
  search: '',
  sortBy: 'sourceUpdatedAt',
  sortDirection: 'desc',
  fileType: '',
  status: '',
  projectNumber: '',
  extractionMethod: '',
};

const EMPTY_SUMMARY = {
  total: null,
  indexed: null,
  notIndexed: null,
  failed: null,
  processing: null,
  needsReindex: null,
};

const EMPTY_PAGE = {
  items: [],
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 0,
  summary: EMPTY_SUMMARY,
};

const SUMMARY_ITEMS = [
  { key: 'total', label: 'Total Documents' },
  { key: 'indexed', label: 'Indexed' },
  { key: 'notIndexed', label: 'Not Indexed' },
  { key: 'failed', label: 'Failed' },
  // { key: 'processing', label: 'Processing' },
  // { key: 'needsReindex', label: 'Needs Reindex' },
];

const SORT_FIELDS = {
  title: 'title',
  projectNumber: 'projectNumber',
  clientName: 'clientName',
  status: 'status',
  sourceUpdatedAt: 'sourceUpdatedAt',
};

const PAGE_SIZES = [25, 50, 100];

const getPositiveInteger = (value, fallback) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const readQueryFromUrl = (search) => {
  const params = new URLSearchParams(search);
  const status = params.get('status') || '';
  const fileType = params.get('fileType') || '';
  const requestedPageSize = getPositiveInteger(params.get('pageSize'), INITIAL_QUERY.pageSize);
  const requestedSortBy = params.get('sortBy');
  const requestedSortDirection = params.get('sortDirection');

  return {
    ...INITIAL_QUERY,
    page: getPositiveInteger(params.get('page'), INITIAL_QUERY.page),
    pageSize: PAGE_SIZES.includes(requestedPageSize) ? requestedPageSize : INITIAL_QUERY.pageSize,
    search: params.get('search') || '',
    status: statusOptions.some((option) => option.value === status) ? status : '',
    fileType: fileTypeOptions.some((option) => option.value === fileType) ? fileType : '',
    sortBy: Object.values(SORT_FIELDS).includes(requestedSortBy) ? requestedSortBy : INITIAL_QUERY.sortBy,
    sortDirection: requestedSortDirection === 'asc' || requestedSortDirection === 'desc' ? requestedSortDirection : INITIAL_QUERY.sortDirection,
    projectNumber: params.get('projectNumber') || '',
    extractionMethod: params.get('extractionMethod') || '',
  };
};

const queriesMatch = (left, right) => Object.keys(INITIAL_QUERY).every((key) => left[key] === right[key]);

const setOptionalUrlParam = (params, name, value) => {
  if (value === '') params.delete(name);
  else params.set(name, value);
};

const ACTIONS = {
  index: {
    label: 'Index',
    progressLabel: 'Indexing...',
    service: aiSystemDocumentService.index,
    successMessage: 'System document indexed successfully.',
  },
  reindex: {
    label: 'Reindex',
    progressLabel: 'Reindexing...',
    service: aiSystemDocumentService.reindex,
    successMessage: 'System document re-indexed successfully.',
  },
  retry: {
    label: 'Retry',
    progressLabel: 'Retrying...',
    service: aiSystemDocumentService.retry,
    successMessage: 'System document indexing retried successfully.',
  },
  remove: {
    label: 'Remove Index',
    progressLabel: 'Removing...',
    service: aiSystemDocumentService.removeFromAi,
    successMessage: 'System document index removed successfully.',
    confirmationText: 'Remove this document from the AI Knowledge Base? The original AES document will not be deleted. It can be indexed again later.',
  },
};

const buildRequestParams = (query) => {
  const params = {
    page: query.page,
    pageSize: query.pageSize,
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
  };

  ['search', 'fileType', 'status', 'projectNumber', 'extractionMethod'].forEach((key) => {
    if (query[key] !== '') params[key] = typeof query[key] === 'string' ? query[key].trim() : query[key];
  });

  return params;
};

export { ACTIONS, buildRequestParams, readQueryFromUrl };

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const SystemDocumentsTab = ({ showAlert, showConfirmModal, hideConfirmModal }) => {
  const history = useHistory();
  const location = useLocation();
  const initialQuery = useRef(readQueryFromUrl(location.search));
  const [query, setQuery] = useState(initialQuery.current);
  const [pageData, setPageData] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(initialQuery.current.search);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeDocumentActions, setActiveDocumentActions] = useState({});
  const [resourceDocument, setResourceDocument] = useState(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState('');
  const [resourceObjectUrl, setResourceObjectUrl] = useState('');
  const [resourcePreviewType, setResourcePreviewType] = useState('other');
  const listRequestId = useRef(0);
  const resourceRequestId = useRef(0);
  const resourceObjectUrlRef = useRef('');
  const activeDocumentActionsRef = useRef({});
  const isMountedRef = useRef(true);
  const urlUpdateMode = useRef('replace');
  const queryRef = useRef(query);
  queryRef.current = query;

  const updateQuery = useCallback((updater, mode = 'push') => {
    urlUpdateMode.current = mode;
    setQuery(updater);
  }, []);

  const clearResourceObjectUrl = useCallback(() => {
    if (resourceObjectUrlRef.current) URL.revokeObjectURL(resourceObjectUrlRef.current);
    resourceObjectUrlRef.current = '';
    setResourceObjectUrl('');
    setResourcePreviewType('other');
  }, []);

  const loadDocuments = useCallback((requestQuery = queryRef.current) => {
    const requestId = ++listRequestId.current;
    setLoading(true);

    return aiSystemDocumentService
      .list(buildRequestParams(requestQuery))
      .then((response) => {
        if (requestId !== listRequestId.current) return;
        setPageData({
          items: Array.isArray(response?.items) ? response.items : [],
          page: response?.page || requestQuery.page,
          pageSize: response?.pageSize || requestQuery.pageSize,
          totalItems: response?.totalItems || 0,
          totalPages: response?.totalPages || 0,
          summary: response?.summary || EMPTY_SUMMARY,
        });
      })
      .catch((error) => {
        if (requestId !== listRequestId.current) return;
        setPageData({ ...EMPTY_PAGE, page: requestQuery.page, pageSize: requestQuery.pageSize });
        showAlert({ message: getErrorMessage(error, 'System documents could not be loaded.'), variant: 'error' });
      })
      .finally(() => {
        if (requestId === listRequestId.current) setLoading(false);
      });
  }, [showAlert]);

  useEffect(() => {
    loadDocuments(query);
  }, [loadDocuments, query]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextSearch = searchInput.trim();

      updateQuery((current) => {
        if (current.search === nextSearch) return current;

        return { ...current, search: nextSearch, page: 1 };
      }, 'replace');
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput, updateQuery]);

  useEffect(() => {
    const nextQuery = readQueryFromUrl(location.search);
    setSearchInput(nextQuery.search);
    setQuery((current) => {
      if (queriesMatch(current, nextQuery)) return current;
      urlUpdateMode.current = 'replace';
      return nextQuery;
    });
  }, [location.search]);

  useEffect(() => {
    const currentLocation = history.location;
    const params = new URLSearchParams(currentLocation.search);
    params.set('page', query.page);
    params.set('pageSize', query.pageSize);
    params.set('sortBy', query.sortBy);
    params.set('sortDirection', query.sortDirection);
    setOptionalUrlParam(params, 'search', query.search);
    setOptionalUrlParam(params, 'status', query.status);
    params.delete('sourceKind');
    setOptionalUrlParam(params, 'fileType', query.fileType);
    setOptionalUrlParam(params, 'projectNumber', query.projectNumber);
    setOptionalUrlParam(params, 'extractionMethod', query.extractionMethod);

    const nextSearch = `?${params.toString()}`;
    if (nextSearch === currentLocation.search) {
      urlUpdateMode.current = 'push';
      return;
    }

    const method = urlUpdateMode.current;
    urlUpdateMode.current = 'push';
    history[method]({ ...currentLocation, search: nextSearch });
  }, [history, query]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      listRequestId.current += 1;
      resourceRequestId.current += 1;
      if (resourceObjectUrlRef.current) URL.revokeObjectURL(resourceObjectUrlRef.current);
    },
    [],
  );

  const closeResourceModal = () => {
    resourceRequestId.current += 1;
    clearResourceObjectUrl();
    setResourceDocument(null);
    setResourceLoading(false);
    setResourceError('');
  };

  const viewResource = (document) => {
    if (document.canView !== true) return;
    const requestId = ++resourceRequestId.current;
    clearResourceObjectUrl();
    setResourceDocument(document);
    setResourceLoading(true);
    setResourceError('');

    aiSystemDocumentService
      .getResource(document.sourceKind, document.sourceDocumentId)
      .then((blob) => {
        if (requestId !== resourceRequestId.current) return;
        const objectUrl = URL.createObjectURL(blob);
        resourceObjectUrlRef.current = objectUrl;
        setResourceObjectUrl(objectUrl);
        setResourcePreviewType(getResourcePreviewType(blob.type, document.fileName || document.title));
      })
      .catch((error) => {
        if (requestId !== resourceRequestId.current) return;
        const message = getErrorMessage(error, 'The source document could not be opened.');
        setResourceError(message);
        showAlert({ message, variant: 'error' });
        loadDocuments();
      })
      .finally(() => {
        if (requestId === resourceRequestId.current) setResourceLoading(false);
      });
  };

  const runDocumentAction = async (document, actionName) => {
    const action = ACTIONS[actionName];
    if (!action) return;

    hideConfirmModal();
    const documentKey = `${document.sourceKind}-${document.sourceDocumentId}`;
    if (activeDocumentActionsRef.current[documentKey]) return;

    activeDocumentActionsRef.current = {
      ...activeDocumentActionsRef.current,
      [documentKey]: actionName,
    };
    setActiveDocumentActions(activeDocumentActionsRef.current);

    const refreshDocumentRow = async () => {
      const updatedDocument = await aiSystemDocumentService.get(
        document.sourceKind,
        document.sourceDocumentId,
      );
      if (!isMountedRef.current) return;
      setPageData((current) => patchSystemDocumentPage(current, updatedDocument, queryRef.current.status));
      setSelectedDocument((current) => {
        if (!current) return current;
        return current.sourceKind === updatedDocument.sourceKind &&
          current.sourceDocumentId === updatedDocument.sourceDocumentId
          ? updatedDocument
          : current;
      });
    };

    try {
      await action.service(document.sourceKind, document.sourceDocumentId);
      if (isMountedRef.current) {
        await refreshDocumentRow();
        showAlert({ message: action.successMessage, variant: 'success' });
      }
    } catch (error) {
      if (isMountedRef.current) {
        try {
          await refreshDocumentRow();
        } catch {
          // Preserve the row and operation error when the focused refresh is unavailable.
        }
        showAlert({ message: getErrorMessage(error, 'The system document operation could not be completed.'), variant: 'error' });
      }
    } finally {
      const nextDocumentActions = { ...activeDocumentActionsRef.current };
      delete nextDocumentActions[documentKey];
      activeDocumentActionsRef.current = nextDocumentActions;
      if (isMountedRef.current) setActiveDocumentActions(nextDocumentActions);
    }
  };

  const confirmDocumentAction = (document, actionName) => {
    const action = ACTIONS[actionName];
    if (!action) return;

    showConfirmModal({
      title: `${action.label} System Document`,
      text: action.confirmationText || `${action.label} the selected system document for the AES Assistant?`,
      rightBtnText: action.label,
      btnAction: () => runDocumentAction(document, actionName),
    });
  };

  const changeFilter = (name, value) => {
    updateQuery((current) => ({ ...current, [name]: value, page: 1 }));
  };

  const renderActions = (dataIndex) => {
    const document = pageData.items[dataIndex];
    if (!document) return null;

    const documentKey = `${document.sourceKind}-${document.sourceDocumentId}`;
    const activeActionName = activeDocumentActions[documentKey];
    const activeAction = ACTIONS[activeActionName];
    const isBusy = Boolean(activeAction);
    const canManageIndex = document.canReindex === true || document.canRemoveFromAi === true;

    return (
      <div className='aes-admin-listing-actions aes-system-document-actions'>
        {document.canView === true && (
          <button type='button' className='btn btn-sm btn-primary font-weight-bold py-2 px-3' onClick={() => viewResource(document)}>
            View
          </button>
        )}

        {document.canIndex === true && (
          <button type='button' className='btn btn-sm btn-warning font-weight-bold py-2 px-3' disabled={isBusy} onClick={() => confirmDocumentAction(document, 'index')}>
            {activeActionName === 'index' ? activeAction.progressLabel : 'Index'}
          </button>
        )}

        {document.canRetry === true && (
          <button type='button' className='btn btn-sm btn-warning font-weight-bold py-2 px-3' disabled={isBusy} onClick={() => confirmDocumentAction(document, 'retry')}>
            {activeActionName === 'retry' ? activeAction.progressLabel : 'Index'}
          </button>
        )}

        {canManageIndex && (
          <Dropdown className='aes-system-document-dropdown aes-system-document-dropdown--index'>
            <Dropdown.Toggle variant='warning' size='sm' className='btn btn-sm btn-warning bg-white text-warning font-weight-bold py-2 px-3 aes-system-document-dropdown__toggle' disabled={isBusy}>
              {activeActionName === 'reindex' || activeActionName === 'remove' ? activeAction.progressLabel : 'Manage'}
            </Dropdown.Toggle>

            <Dropdown.Menu className='aes-system-document-dropdown__menu'>
              {document.canReindex === true && (
                <Dropdown.Item className='aes-system-document-dropdown__item text-warning' onClick={() => confirmDocumentAction(document, 'reindex')}>
                  Reindex
                </Dropdown.Item>
              )}

              {document.canRemoveFromAi === true && (
                <Dropdown.Item className='aes-system-document-dropdown__item text-danger' onClick={() => confirmDocumentAction(document, 'remove')}>
                  Remove Index
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}

        <Dropdown className='aes-system-document-dropdown aes-system-document-dropdown--more'>
          <Dropdown.Toggle variant='info' size='sm' className='btn btn-sm btn-info bg-white text-info border-info font-weight-bold py-2 px-3 aes-system-document-dropdown__toggle'>
            Details
          </Dropdown.Toggle>

          <Dropdown.Menu className='aes-system-document-dropdown__menu'>
            <Dropdown.Item className='aes-system-document-dropdown__item text-info' onClick={() => setSelectedDocument(document)}>
              Document Info
            </Dropdown.Item>

            {document.navigation?.url && (
              <Dropdown.Item className='aes-system-document-dropdown__item text-info' as={Link} to={document.navigation.url}>
                View Source
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const columns = [
    {
      name: 'sourceDocumentId',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        download: false,
        setCellProps: () => ({ 'data-aes-field': 'actions', 'data-aes-label': 'Actions' }),
        customBodyRenderLite: renderActions,
      },
    },
    {
      name: 'title',
      label: 'Document',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'document', 'data-aes-label': 'Document' }),
        customBodyRenderLite: (dataIndex) => {
          const document = pageData.items[dataIndex];
          return <div className='font-weight-bolder text-dark text-break'>{document?.title || document?.fileName || <>&mdash;</>}</div>;
        },
      },
    },
    {
      name: 'projectNumber',
      label: 'Project',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'project', 'data-aes-label': 'Project' }),
        customBodyRenderLite: (dataIndex) => {
          const document = pageData.items[dataIndex];
          return (
            <div>
              <div className='font-weight-bolder text-dark'>{document?.projectNumber || <>&mdash;</>}</div>
              {document?.projectName && <div className='text-muted font-size-sm'>{document.projectName}</div>}
            </div>
          );
        },
      },
    },
    {
      name: 'clientName',
      label: 'Client / Location',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'client-location', 'data-aes-label': 'Client / Location' }),
        customBodyRenderLite: (dataIndex) => {
          const document = pageData.items[dataIndex];
          const showLocation = document?.locationName && document.locationName.trim().toLowerCase() !== (document.clientName || '').trim().toLowerCase();
          return (
            <div>
              <div className='font-weight-bold text-dark'>{document?.clientName || <>&mdash;</>}</div>
              {showLocation && <div className='text-muted font-size-sm'>{document.locationName}</div>}
            </div>
          );
        },
      },
    },
    {
      name: 'sourceKind',
      label: 'Source',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ 'data-aes-field': 'source', 'data-aes-label': 'Source' }),
        customBodyRenderLite: (dataIndex) => getSourceKindLabel(pageData.items[dataIndex]?.sourceKind),
      },
    },
    {
      name: 'status',
      label: 'AI Status',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'status', 'data-aes-label': 'AI Status' }),
        customBodyRender: (value) => {
          const status = getStatusMetadata(value);
          return (
            <span
              className={`label label-inline text-nowrap ${status.badgeClass} font-weight-bolder text-uppercase`}
              style={{ position: 'static', top: 'auto', right: 'auto', opacity: 1 }}
            >
              {status.label}
            </span>
          );
        },
      },
    },
    {
      name: 'extractionMethod',
      label: 'Extraction',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ 'data-aes-field': 'extraction', 'data-aes-label': 'Extraction' }),
        customBodyRenderLite: (dataIndex) => getExtractionMethodLabel(pageData.items[dataIndex]?.extractionMethod) || <>&mdash;</>,
      },
    },
    {
      name: 'sourceUpdatedAt',
      label: 'Source Updated',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'updated', 'data-aes-label': 'Source Updated' }),
        customBodyRenderLite: (dataIndex) => formatDateTime(pageData.items[dataIndex]?.sourceUpdatedAt) || <>&mdash;</>,
      },
    },
  ];

  const handleTableChange = (action, tableState) => {
    if (action === 'changePage') {
      updateQuery((current) => ({ ...current, page: tableState.page + 1 }));
      return;
    }

    if (action === 'changeRowsPerPage') {
      updateQuery((current) => ({ ...current, page: 1, pageSize: tableState.rowsPerPage }));
      return;
    }

    if (action === 'sort') {
      const sortField = SORT_FIELDS[tableState.sortOrder?.name];
      const sortDirection = tableState.sortOrder?.direction;
      if (sortField && (sortDirection === 'asc' || sortDirection === 'desc')) {
        updateQuery((current) => ({ ...current, page: 1, sortBy: sortField, sortDirection }));
      }
    }
  };

  return (
    <div className='px-6 py-5 aes-system-documents'>
      <div className='mb-18 px-3'>
        <h4 className='font-size-h3 font-weight-bolder text-dark mb-1'>System Documents</h4>
        <div className='text-muted fz-xs mb-6'>Documents owned by AES projects, proposals, jobs, and other source records.</div>

        <div className='aes-knowledge-summary mb-12' aria-label='System document summary'>
          {SUMMARY_ITEMS.map((item) => {
            const value = pageData.summary?.[item.key];
            return (
              <div key={item.key} className='aes-knowledge-summary__item'>
                <div className='text-muted font-size-md'>{item.label}</div>
                <div className='text-dark font-size-h5 font-weight-bolder'>{value === null || value === undefined ? <>&mdash;</> : value.toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        <div className='row aes-system-document-filters'>
          <div className='col-12 col-lg-6 mb-3'>
            <Form.Label>Search</Form.Label>
            <Form.Control value={searchInput} placeholder='Search documents, projects, clients, or locations' onChange={(event) => setSearchInput(event.target.value)} />
          </div>
          <div className='col-12 col-sm-6 col-lg-3 mb-3'>
            <Form.Label>AI Status</Form.Label>
            <Form.Control as='select' value={query.status} onChange={(event) => changeFilter('status', event.target.value)}>
              <option value=''>All</option>
              {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Control>
          </div>
          <div className='col-12 col-sm-6 col-lg-3 mb-3'>
            <Form.Label>File Type</Form.Label>
            <Form.Control as='select' value={query.fileType} onChange={(event) => changeFilter('fileType', event.target.value)}>
              <option value=''>All</option>
              {fileTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Control>
          </div>
        </div>
      </div>

      {pageData.totalItems > 0 && (
        <div className='text-muted font-size-sm text-right mb-2'>
          Page {pageData.page} of {pageData.totalPages} ({pageData.totalItems.toLocaleString()} documents)
        </div>
      )}

      <div className='container-fluid aes-admin-listing p-0'>
        <MUIDataTable
          className='aes-admin-listing-surface'
          data={pageData.items}
          columns={columns}
          options={{
            responsive: 'standard',
            serverSide: true,
            count: pageData.totalItems,
            page: Math.max(0, query.page - 1),
            rowsPerPage: query.pageSize,
            rowsPerPageOptions: [25, 50, 100],
            selectableRows: 'none',
            search: false,
            filter: false,
            download: false,
            print: false,
            viewColumns: false,
            sortOrder: { name: query.sortBy, direction: query.sortDirection },
            onTableChange: handleTableChange,
            textLabels: { body: { noMatch: loading ? 'Loading system documents...' : 'No system documents match the current filters.' } },
            setTableProps: () => ({ className: 'aes-admin-listing-table aes-system-documents-table' }),
          }}
        />
      </div>

      <SystemDocumentDetailsModal show={Boolean(selectedDocument)} onHide={() => setSelectedDocument(null)} document={selectedDocument} />

      <ModalBlock
        centered
        size='xl'
        show={Boolean(resourceDocument)}
        onHide={closeResourceModal}
        closeAction
        closeActionOnClick={closeResourceModal}
        contentHeader={resourceDocument?.title || 'System Document'}
        subtitle={getSourceKindLabel(resourceDocument?.sourceKind)}
      >
        <div className='mt-5'>
          {resourceLoading ? (
            <div className='text-muted text-center py-10'>Loading document resource...</div>
          ) : resourceError ? (
            <div className='alert alert-danger' role='alert'>{resourceError}</div>
          ) : resourceObjectUrl ? (
            <div>
              <div className='d-flex justify-content-end mb-3'>
                <a className='btn btn-primary' href={resourceObjectUrl} target='_blank' rel='noopener noreferrer'>Open in New Tab</a>
              </div>
              {resourcePreviewType === 'pdf' ? (
                <iframe title={resourceDocument?.title || 'System document resource'} src={resourceObjectUrl} style={{ width: '100%', height: '60vh', border: 0 }} />
              ) : resourcePreviewType === 'image' ? (
                <img src={resourceObjectUrl} alt={resourceDocument?.title || resourceDocument?.fileName || 'System document'} style={{ display: 'block', maxWidth: '100%', maxHeight: '60vh', margin: '0 auto' }} />
              ) : (
                <div className='text-muted text-center py-10'>This resource cannot be previewed in the browser. Use Open in New Tab.</div>
              )}
            </div>
          ) : null}
        </div>
      </ModalBlock>
    </div>
  );
};

export default SystemDocumentsTab;
